// The heroes in captivity, read off the snapshot (`globals.heroes`, module
// `heroes.captivity`). The client keeps no copy: a captive who is freed,
// ransomed or put to death simply leaves the snapshot.
//
// A captive is off the board — they have no hex and are not drawn on it — so
// this is where the board's absence is shown. The captor decides what
// becomes of them; the captive's house may buy them back.

import type { GameSnapshot } from '@aod/protocol';

/** What a captor has decided to do with the hero they hold. */
export type HeroCaptivityChoice = 'undecided' | 'ransom' | 'hold' | 'execute' | 'release';

/** A hero in captivity, and what their captor means to do with them. */
export interface HeroCaptive {
  /** The hero's entity id — the same number the unit node id carries. */
  readonly id: number;
  readonly name: string;
  readonly archetype: string;
  readonly level: number;
  /** The house the hero belongs to (the one that may buy them back). Null
   *  when the sim does not know it — a hero with no owning house. */
  readonly owner: number | null;
  /** The house holding them. */
  readonly captor: number;
  /** The tick the capture began. */
  readonly since: number;
  readonly choice: HeroCaptivityChoice;
  /** The ransom asked, in gold (only meaningful for a ransom). */
  readonly price: number;
  /** The tick the ransom runs out. */
  readonly deadline: number;
  /** Where the hero was taken — a captive is no longer at a hex. */
  readonly at: { readonly q: number; readonly r: number } | null;
}

export interface HeroCaptivity {
  readonly captives: readonly HeroCaptive[];
}

/** The hero captivity the sim published, or null while the module is off. */
export function readHeroCaptivity(snapshot: GameSnapshot | null): HeroCaptivity | null {
  const globals = snapshot?.state.globals as
    | { heroes?: { captives?: WireHeroCaptive[] } }
    | undefined;
  const raw = globals?.heroes;
  if (!raw) return null;
  return {
    captives: (raw.captives ?? []).map((c) => ({
      ...c,
      owner: c.owner ?? null,
      at: c.at ?? null,
    })),
  };
}

/**
 * A captive as the wire sends it. The wasm wire sends an absent `Option` as
 * `undefined`, not `null` (serde_wasm_bindgen), so the read normalises the
 * optional fields once, here, instead of at each call site.
 */
type WireHeroCaptive = Omit<HeroCaptive, 'owner' | 'at'> & {
  owner?: number | null;
  at?: { readonly q: number; readonly r: number } | null;
};

/** The node id the sim knows this hero by — `unit_<entity id>`. */
export function heroUnitId(captive: HeroCaptive): string {
  return `unit_${captive.id}`;
}

/** The heroes a house holds, in id order. */
export function captivesHeld(
  captivity: HeroCaptivity | null,
  seat: number,
): readonly HeroCaptive[] {
  return (captivity?.captives ?? []).filter((c) => c.captor === seat);
}

/** The heroes taken from a house, in id order. */
export function captivesTaken(
  captivity: HeroCaptivity | null,
  seat: number,
): readonly HeroCaptive[] {
  return (captivity?.captives ?? []).filter((c) => c.owner === seat);
}

/** May this house buy this hero back? Only the house the hero belongs to,
 *  and only while a ransom is on the table. */
export function mayPayHeroRansom(
  captivity: HeroCaptivity | null,
  payer: number,
  heroId: number,
): boolean {
  const captive = (captivity?.captives ?? []).find((c) => c.id === heroId);
  if (!captive) return false;
  return captive.owner === payer && captive.choice === 'ransom';
}

/** The four things a captor can do with a hero they hold — the same four a
 *  captor can do with a royal, and the same four the sim accepts. */
export const HERO_CAPTIVITY_CHOICES = ['ransom', 'hold', 'execute', 'release'] as const;
